import React, {
	useState,
	useEffect,
	useRef,
	useCallback,
} from '@wordpress/element';
import useShadowDOM from '../hooks/useShadowDOM';
import useYouTubePlayer from '../hooks/useYouTubePlayer';
import useDimensions from '../hooks/useDimensions';
import { createShadowStyles } from '../utils/shadowStyles';

/**
 * Main PingVid Widget Component
 * Renders a floating video widget with expand/collapse functionality
 * @param root0
 * @param root0.settings
 * @param root0.videoId
 */
const PingVidWidget = ({ settings, videoId }) => {
	const [expanded, setExpanded] = useState(false);
	const [visible, setVisible] = useState(false);
	const [transitioning, setTransitioning] = useState(false);

	const containerRef = useRef(null);
	const playerRef = useRef(null);

	// Extract settings with defaults
	const {
		position = 'bottom-right',
		margin_x = '20px',
		margin_y = '20px',
		delay = 3000,
		aspect_ratio = '9:16',
	} = settings?.display || {};

	// Custom hooks
	const { getDimensions, getExpandedDimensions } =
		useDimensions(aspect_ratio);
	const { shadowContainerRef, shadowReactRootRef } = useShadowDOM(
		containerRef,
		createShadowStyles(getDimensions(), aspect_ratio, margin_x, margin_y)
	);
	const playerReady = useYouTubePlayer(
		playerRef,
		shadowContainerRef,
		visible,
		videoId
	);

	// Show widget after delay
	useEffect(() => {
		const timer = setTimeout(() => {
			setVisible(true);
		}, delay + 1000); // Add 1s for video to start loading

		return () => clearTimeout(timer);
	}, [delay]);

	// Handle expand
	const handleExpand = useCallback(() => {
		if (expanded || transitioning) {
			return;
		}

		setTransitioning(true);

		// Start expansion animation
		requestAnimationFrame(() => {
			if (shadowContainerRef.current) {
				const wrapper =
					shadowContainerRef.current.querySelector(
						'#pingvid-wrapper'
					);
				if (wrapper) {
					wrapper.classList.add('transitioning');

					const isMobile = window.innerWidth <= 768;

					if (isMobile) {
						wrapper.classList.add('mobile-expanded');
						// 	// Mobile: Center the video
						// 	const dims = getExpandedDimensions();
						// 	if (dims) {
						// 		// Clear position-based classes temporarily
						// 		wrapper.classList.remove(
						// 			'bottom-right',
						// 			'bottom-left',
						// 			'top-right',
						// 			'top-left'
						// 		);

						// 		// Apply inline styles for centering
						// 		Object.assign(wrapper.style, {
						// 			position: 'fixed',
						// 			bottom: 'auto',
						// 			right: 'auto',
						// 			top: '50%',
						// 			left: '50%',
						// 			transform: 'translate(-50%, -50%)',
						// 			width: `${dims.width}px`,
						// 			height: `${dims.height}px`,
						// 			margin: '0',
						// 		});
						// 	}
					}
					// Desktop expansion is handled purely by CSS with 2.77x multiplier

					// Add expanded class after styles are applied
					requestAnimationFrame(() => {
						wrapper.classList.add('expanded');
						setExpanded(true);

						// Hide overlay
						const overlay =
							shadowContainerRef.current?.querySelector(
								'#pingvid-overlay'
							);
						if (overlay) {
							overlay.style.display = 'none';
						}

						// End transition state
						setTimeout(() => {
							setTransitioning(false);
							wrapper.classList.remove('transitioning');
						}, 400);
					});
				}
			}
		});

		// After expand animation finishes, then refresh video
		setTimeout(() => {
			const player = playerRef.current;
			if (!player) {
				return;
			}

			player.unMute();
			player.seekTo(0);

			// play video first
			player.playVideo();

			// reload source a bit later to avoid reflow while expanding
			setTimeout(() => {
				player.loadVideoById({
					videoId,
					suggestedQuality: 'hd720',
				});
			}, 300);
		}, 500);
	}, [
		expanded,
		transitioning,
		aspect_ratio,
		getExpandedDimensions,
		shadowContainerRef,
	]);

	// Handle close
	const handleClose = useCallback(() => {
		if (shadowContainerRef.current) {
			const wrapper =
				shadowContainerRef.current.querySelector('#pingvid-wrapper');
			if (wrapper) {
				wrapper.style.display = 'none';
				playerRef.current.destroy?.();
			}
		}
		if (playerRef.current && playerRef.current.destroy) {
			playerRef.current.destroy();
		}
	}, [shadowContainerRef]);

	// Render into shadow DOM
	useEffect(() => {
		// Small delay to ensure React root is ready
		const renderTimer = setTimeout(() => {
			if (!shadowReactRootRef.current || !shadowContainerRef.current) {
				console.warn('PingVid: React root not ready');
				return;
			}

			const content = (
				<div
					id="pingvid-wrapper"
					className={`${position} ${visible ? 'visible' : ''} ${expanded ? 'expanded' : ''} ${transitioning ? 'transitioning' : ''}`}
					data-aspect={aspect_ratio}
				>
					<div
						id="pingvid-overlay"
						className={expanded ? 'hidden' : ''}
						onClick={handleExpand}
					/>
					<div id="pingvid-video-container">
						<div id="pingvid-iframe" />
					</div>
					<button id="pingvid-close" onClick={handleClose}>
						✕
					</button>
				</div>
			);

			shadowReactRootRef.current(content);
		}, 50);

		return () => clearTimeout(renderTimer);
	}, [
		visible,
		expanded,
		transitioning,
		position,
		aspect_ratio,
		handleExpand,
		handleClose,
		shadowReactRootRef,
		shadowContainerRef,
	]);

	return <div ref={containerRef} id="pingvid-react-container" />;
};

export default PingVidWidget;
